/* eslint-disable no-unused-vars */
import PropTypes from 'prop-types';
import { useState, useCallback, useEffect, useRef } from 'react';
import { isSameDay, isSameWeek, isSameMonth } from 'date-fns';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Divider,
  useTheme
} from '@mui/material';
import CustomerRelanceComment from '../customer/CustomerRelanceComment';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PencilAltIcon from '../../../icons/PencilAlt';
import * as Yup from 'yup';
import { Formik } from 'formik';
import toast from 'react-hot-toast';
import { customerApi } from '../../../api/customerApi';
import useMounted from '../../../hooks/useMounted';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { toneAlpha } from '../../../theme/tones';
import usePermissions from '../../../hooks/usePermissions';

const RelaunchCustomerTabRelaunch = (props) => {
  const { customerId, user, setIsTreated, setCommentRelance, ...other } = props;
  const { can } = usePermissions();
  const theme = useTheme();
  
  const todayItems = [];
  const thisWeekItems = [];
  const thisMonthItems = [];
  const ancientItems = [];

  const isAuthorized = can('customers.comment_relance', [0, 4].includes(user.id_service) || user.access_level === 0);

  const mounted = useMounted();
  const [comments, setComments] = useState([]);
  const [commenting, setCommenting] = useState(false);
  const navigate = useNavigate();
  const relanceLabel = ['Pas répondu', 'À rappeler', 'Numéro incorrect', 'Va se connecter', 'Suivi - Téléphone', 'Suivi - Mail', 'Suivi - SMS', 'Décalage', 'Annulation'];
  const [choiceRelance, setChoiceRelance] = useState(null);
  const [choiceQualificationRecall, setChoiceQualificationRecall] = useState(null);
  const [displayDateRecall, setDisplayDateRecall] = useState(false);
  const [isFormFilled, setIsFormFilled] = useState(true);

  useEffect(() => {
    if (choiceRelance !== null && choiceRelance !== 'À rappeler') {
      setIsFormFilled(false);
    } else {
      setIsFormFilled(true);
    }
    if (choiceRelance !== null && choiceRelance === 'À rappeler' && choiceQualificationRecall !== null && choiceQualificationRecall !== '') {
      setIsFormFilled(false);
    }
  }, [choiceRelance, choiceQualificationRecall]);

  const handleChangeRelance = (value) => {
    setChoiceRelance(value);
    switch (value) {
      case 'À rappeler':
        setDisplayDateRecall(true);
        break;
      default:
        setDisplayDateRecall(false);
        break;
    }
  };

  const handleDelete = async (id) => {
    try {
      await customerApi.deleteCommentRelance(id, localStorage.getItem('accessToken'));
      window.location.reload();
    } catch (err) {
      console.error(err);
      toast.error('Il y a eu un souci lors de la suppression !');
    }
  };

  for (const activity of comments) {
    if (isSameDay(new Date(activity.createdAt), new Date())) {
      todayItems.push(activity);
    } else if (isSameWeek(new Date(activity.createdAt), new Date())) {
      thisWeekItems.push(activity);
    } else if (isSameMonth(new Date(activity.createdAt), new Date())) {
      thisMonthItems.push(activity);
    } else {
      ancientItems.push(activity);
    }
  }

  const formikRef = useRef();
  useEffect(() => {
    setChoiceQualificationRecall(null);
    if (formikRef.current) {
      formikRef.current.values.choiceQualificationRecall = null;
    }
  }, [choiceRelance]);

  const getCustomer = useCallback(async () => {
    try {
      const data = await customerApi.getCustomer(customerId, localStorage.getItem('accessToken'));
      if (data.length === 0) {
        throw new Error('L\'apprenant n\'existe pas');
      }
      if (mounted.current) {
        setComments(data.relance_comments);
      }
    } catch (err) {
      console.error(err);
      navigate('/404', { replace: true });
    }
  }, [mounted, customerId, navigate]);

  useEffect(() => {
    getCustomer();
  }, [getCustomer]);

  const submitCommentSpecial = async (comment, qualification) => {
    try {
      const jsonAnswer = await customerApi.addCommentRelanceSpecial(customerId, user.id, comment, qualification, localStorage.getItem('accessToken'));
      toast.success('Commentaire de relance enregistré !');
      const tempComments = [...comments];
      tempComments.unshift({
        id: jsonAnswer.data.customer_id,
        comment: jsonAnswer.data.comment,
        createdAt: jsonAnswer.data.created_at,
        firstname: user.firstname,
        lastname: user.lastname,
        qualification: jsonAnswer.data.qualification,
      });
      setIsTreated(true);
      setComments(tempComments);
    } catch (err) {
      console.error(err);
      toast.error('Il y a eu un souci lors de l\'enregistrement !');
    }
  };

  return (
    <div {...other}>
      <Formik
        innerRef={formikRef}
        initialValues={{ commentRelance: '', choiceRelance: null, choiceQualificationRecall: '' }}
        enableReinitialize
        validationSchema={Yup.object().shape({
          commentRelance: Yup.string().nullable(),
          choiceRelance: Yup.string().nullable(),
          choiceQualificationRecall: Yup.string().nullable()
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting, resetForm }) => {
          try {
            const jsonAnswer = await customerApi.addCommentRelance(customerId, user.id, values, localStorage.getItem('accessToken'));
            setStatus({ success: true });
            resetForm({});
            setSubmitting(false);
            toast.success('Commentaire de relance enregistré !');
            setCommenting(false);
            const tempComments = [...comments];
            tempComments.unshift({
              id: jsonAnswer.data.customer_id,
              comment: jsonAnswer.data.comment,
              createdAt: jsonAnswer.data.created_at,
              firstname: user.firstname,
              lastname: user.lastname,
              qualification: choiceRelance,
              recall_date: choiceQualificationRecall
            });
            setComments(tempComments);
            setCommentRelance({
              id: jsonAnswer.data.customer_id,
              comment: jsonAnswer.data.comment,
              createdAt: jsonAnswer.data.created_at,
              firstname: user.firstname,
              lastname: user.lastname,
              qualification_relance: choiceRelance,
              recall_date: choiceQualificationRecall
            });
            setIsTreated(true);
            setChoiceQualificationRecall(null);
            setChoiceRelance(null);
            setDisplayDateRecall(false);
          } catch (err) {
            console.error(err);
            toast.error('Il y a eu un souci lors de l\'enregistrement !');
            setStatus({ success: false });
            setErrors({ submit: err.message });
            setSubmitting(false);
          }
        }}
      >
        {({ errors, handleSubmit, handleBlur, handleChange, touched, values, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            {commenting ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4, p: 3.5, borderRadius: '24px', bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, backdropFilter: 'blur(20px)' }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Laisser un commentaire
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={relanceLabel}
                      onChange={(e, value) => {
                        handleChangeRelance(value);
                        setFieldValue('choiceRelance', value);
                      }}
                      value={values.choiceRelance}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label="Qualification"
                          name="choiceRelance"
                          variant="outlined"
                          InputProps={{
                            ...params.InputProps,
                            sx: { borderRadius: '12px' }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  {displayDateRecall && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        error={Boolean(touched.choiceQualificationRecall && errors.choiceQualificationRecall)}
                        fullWidth
                        helperText={touched.choiceQualificationRecall && errors.choiceQualificationRecall}
                        label="Date et heure du rappel"
                        name="choiceQualificationRecall"
                        onBlur={handleBlur}
                        onChange={(event) => {
                          handleChange(event);
                          setChoiceQualificationRecall(event.target.value);
                        }}
                        type="datetime-local"
                        value={values.choiceQualificationRecall || ''}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ sx: { borderRadius: '12px' } }}
                      />
                    </Grid>
                  )}
                </Grid>
                
                <TextField
                  label="Commentaire"
                  error={Boolean(touched.commentRelance && errors.commentRelance)}
                  fullWidth
                  helperText={touched.commentRelance && errors.commentRelance}
                  multiline
                  name="commentRelance"
                  variant="outlined"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.commentRelance || ''}
                  rows={3}
                  InputProps={{ sx: { borderRadius: '12px' } }}
                />
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    color="primary"
                    startIcon={<SaveIcon fontSize="small" />}
                    variant="contained"
                    disabled={isFormFilled}
                    onClick={handleSubmit}
                    sx={{ 
                      borderRadius: '12px', 
                      textTransform: 'none', 
                      fontWeight: 700, 
                      px: 2.5,
                      background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                      boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.secondary.main, 0.2)}`
                    }}
                  >
                    Enregistrer
                  </Button>
                  <Button
                    color="error"
                    startIcon={<CancelIcon fontSize="small" />}
                    variant="outlined"
                    onClick={() => setCommenting(false)}
                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 2.5 }}
                  >
                    Annuler
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4.5 }}>
                <Button
                  color="primary"
                  startIcon={<PencilAltIcon fontSize="small" />}
                  disabled={!isAuthorized}
                  variant="contained"
                  onClick={() => setCommenting(true)}
                  sx={{ 
                    borderRadius: '12px', 
                    textTransform: 'none', 
                    fontWeight: 700, 
                    px: 3,
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                    boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.secondary.main, 0.2)}`
                  }}
                >
                  Commenter
                </Button>
                <Button
                  color="primary"
                  disabled={!isAuthorized}
                  variant="outlined"
                  onClick={() => submitCommentSpecial('Messagerie', 'Pas répondu')}
                  sx={{ 
                    borderRadius: '12px', 
                    textTransform: 'none', 
                    fontWeight: 700, 
                    px: 2,
                    borderWidth: '1.5px',
                    borderColor: 'primary.main',
                    '&:hover': {
                      borderWidth: '1.5px',
                      backgroundColor: theme.palette.mode === 'light' ? toneAlpha('blue', 0.04) : toneAlpha('blue', 0.1)
                    }
                  }}
                >
                  Messagerie
                </Button>
                <Button
                  color="primary"
                  disabled={!isAuthorized}
                  variant="outlined"
                  onClick={() => submitCommentSpecial('Connexion dans la semaine', 'Va se connecter')}
                  sx={{ 
                    borderRadius: '12px', 
                    textTransform: 'none', 
                    fontWeight: 700, 
                    px: 2,
                    borderWidth: '1.5px',
                    borderColor: 'primary.main',
                    '&:hover': {
                      borderWidth: '1.5px',
                      backgroundColor: theme.palette.mode === 'light' ? toneAlpha('blue', 0.04) : toneAlpha('blue', 0.1)
                    }
                  }}
                >
                  Connexion
                </Button>
                <Button
                  color="primary"
                  disabled={!isAuthorized}
                  variant="outlined"
                  onClick={() => submitCommentSpecial('Campagne email', 'Suivi - Mail')}
                  sx={{ 
                    borderRadius: '12px', 
                    textTransform: 'none', 
                    fontWeight: 700, 
                    px: 2,
                    borderWidth: '1.5px',
                    borderColor: 'primary.main',
                    '&:hover': {
                      borderWidth: '1.5px',
                      backgroundColor: theme.palette.mode === 'light' ? toneAlpha('blue', 0.04) : toneAlpha('blue', 0.1)
                    }
                  }}
                >
                  Email
                </Button>
                <Button
                  color="primary"
                  disabled={!isAuthorized}
                  variant="outlined"
                  onClick={() => submitCommentSpecial('Campagne SMS', 'Suivi - SMS')}
                  sx={{ 
                    borderRadius: '12px', 
                    textTransform: 'none', 
                    fontWeight: 700, 
                    px: 2,
                    borderWidth: '1.5px',
                    borderColor: 'primary.main',
                    '&:hover': {
                      borderWidth: '1.5px',
                      backgroundColor: theme.palette.mode === 'light' ? toneAlpha('blue', 0.04) : toneAlpha('blue', 0.1)
                    }
                  }}
                >
                  SMS
                </Button>
              </Box>
            )}
          </form>
        )}
      </Formik>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
              Aujourd&apos;hui
            </Typography>
            <Box
              sx={{
                px: 1.2,
                py: 0.2,
                borderRadius: '8px',
                fontSize: '0.72rem',
                fontWeight: 700,
                backgroundColor: 'action.hover',
                color: 'text.secondary'
              }}
            >
              {todayItems.length}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {todayItems.length === 0 ? (
              <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.85rem', pl: 2 }}>
                Aucune relance effectuée aujourd'hui
              </Typography>
            ) : (
              todayItems.map((comment) => (
                <CustomerRelanceComment
                  key={comment.id}
                  id={comment.id}
                  createdAt={comment.createdAt}
                  source={`${comment.firstname} ${comment.lastname}`}
                  comment={comment.comment}
                  handleDelete={handleDelete}
                  qualification={comment.qualification}
                  recallDate={comment.recall_date}
                />
              ))
            )}
          </Box>
        </Box>
        
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
              Cette semaine
            </Typography>
            <Box
              sx={{
                px: 1.2,
                py: 0.2,
                borderRadius: '8px',
                fontSize: '0.72rem',
                fontWeight: 700,
                backgroundColor: 'action.hover',
                color: 'text.secondary'
              }}
            >
              {thisWeekItems.length}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {thisWeekItems.length === 0 ? (
              <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.85rem', pl: 2 }}>
                Aucune relance cette semaine
              </Typography>
            ) : (
              thisWeekItems.map((comment) => (
                <CustomerRelanceComment
                  key={comment.id}
                  id={comment.id}
                  createdAt={comment.createdAt}
                  source={`${comment.firstname} ${comment.lastname}`}
                  comment={comment.comment}
                  handleDelete={handleDelete}
                  qualification={comment.qualification}
                  recallDate={comment.recall_date}
                />
              ))
            )}
          </Box>
        </Box>
        
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
              Ce mois-ci
            </Typography>
            <Box
              sx={{
                px: 1.2,
                py: 0.2,
                borderRadius: '8px',
                fontSize: '0.72rem',
                fontWeight: 700,
                backgroundColor: 'action.hover',
                color: 'text.secondary'
              }}
            >
              {thisMonthItems.length}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {thisMonthItems.length === 0 ? (
              <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.85rem', pl: 2 }}>
                Aucune relance ce mois-ci
              </Typography>
            ) : (
              thisMonthItems.map((comment) => (
                <CustomerRelanceComment
                  key={comment.id}
                  id={comment.id}
                  createdAt={comment.createdAt}
                  source={`${comment.firstname} ${comment.lastname}`}
                  comment={comment.comment}
                  handleDelete={handleDelete}
                  qualification={comment.qualification}
                  recallDate={comment.recall_date}
                />
              ))
            )}
          </Box>
        </Box>
        
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
              Plus ancien
            </Typography>
            <Box
              sx={{
                px: 1.2,
                py: 0.2,
                borderRadius: '8px',
                fontSize: '0.72rem',
                fontWeight: 700,
                backgroundColor: 'action.hover',
                color: 'text.secondary'
              }}
            >
              {ancientItems.length}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {ancientItems.length === 0 ? (
              <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.85rem', pl: 2 }}>
                Aucun historique plus ancien
              </Typography>
            ) : (
              ancientItems.map((comment) => (
                <CustomerRelanceComment
                  key={comment.id}
                  id={comment.id}
                  createdAt={comment.createdAt}
                  source={`${comment.firstname} ${comment.lastname}`}
                  comment={comment.comment}
                  handleDelete={handleDelete}
                  qualification={comment.qualification}
                  recallDate={comment.recall_date}
                />
              ))
            )}
          </Box>
        </Box>
      </Box>
    </div>
  );
};

RelaunchCustomerTabRelaunch.propTypes = {
  rComments: PropTypes.array,
  customerId: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  setIsTreated: PropTypes.func,
  setCommentRelance: PropTypes.func
};

export default RelaunchCustomerTabRelaunch;
